import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Code2, Loader2, Sparkles, Square } from "lucide-react";
import Md from "./Md";
import { Button } from "../ui";
import type { InlineVisualSource } from "./source-pane/InlineVisual";
import type { TraceEvent } from "../chatStream";

// Eingabevertrag deckt sich mit dem Source-Vertrag aus ProjectChatView —
// duplikatfrei kann das nur, wenn diese Form irgendwo geteilt ist. Aktuell
// ist das hier eine lokale, lockere Variante (kind optional / unknown).
interface SourceLike {
  kind?: string;
  document_id?: string;
  fig_id?: string;
  table_id?: string;
  page?: number;
  caption?: string | null;
  summary?: string | null;
  html_preview?: string | null;
  // External-Source-Bytes (cosmetics_facts via doc-ai brain_store):
  // wenn beide gesetzt → URL geht über /projects/external-sources/...
  source_hash?: string | null;
  external_source_slug?: string | null;
}

interface StructuredAnswer {
  answer: string;
  result_rows?: Record<string, unknown>[] | null;
  result_columns?: string[] | null;
  result_title?: string | null;
  footnotes?: string[] | null;
  // Meta-Felder mit Underscore-Prefix (Backend-Convention).
  _sql_text?: string | null;
  _query_plan?: string | null;
}

/** Parst content als strukturiertes Antwort-Schema, oder fällt auf
 * Plain-Markdown zurück. So funktioniert dieselbe Bubble in jedem
 * Chat-Kontext — egal ob das Backend strukturiert antwortet oder nicht. */
export function parseAssistantContent(content: string): StructuredAnswer {
  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) return { answer: content };
  try {
    const obj = JSON.parse(trimmed);
    if (obj && typeof obj === "object" && typeof obj.answer === "string") {
      return obj as StructuredAnswer;
    }
  } catch {
    /* not JSON */
  }
  return { answer: content };
}

export default function AssistantMessage({
  content,
  onCite,
  sources,
  streaming = false,
  mode = null,
  liveStatus = null,
  agentTrace = null,
  onCancel,
}: {
  content: string;
  onCite?: (n: number) => void;
  sources?: SourceLike[];
  streaming?: boolean;
  /** D6: 'agent' bei Agent-Mode-Messages — schaltet Mode-Badge + Trace-View. */
  mode?: "agent" | null;
  /** D6 Live-Status-Label (waehrend Stream, z.B. 'Recherchiere ...'). */
  liveStatus?: string | null;
  /** D6 Trace-Liste — entweder live waehrend Stream oder persistiert aus DB. */
  agentTrace?: TraceEvent[] | null;
  /** D6 Stop-Button-Handler. Nur sichtbar waehrend agent-stream. */
  onCancel?: () => void;
}) {
  const s = parseAssistantContent(content);
  const rows = s.result_rows ?? null;
  const cols =
    s.result_columns ??
    (rows && rows.length > 0 ? Object.keys(rows[0]) : null);
  const visualSources = useMemo(
    () => buildVisualSources(sources),
    [sources]
  );
  const showStructured = !streaming;
  const isAgent = mode === "agent";
  const presentationCards = useMemo(
    () => extractPresentationArtifacts(agentTrace),
    [agentTrace],
  );
  const digestEv = useMemo(
    () => (agentTrace ?? []).find((e) => e.type === "digest") ?? null,
    [agentTrace],
  );
  const planEv = useMemo(
    () => (agentTrace ?? []).find((e) => e.type === "plan") ?? null,
    [agentTrace],
  );
  const storylineEv = useMemo(
    () => (agentTrace ?? []).find((e) => e.type === "storyline") ?? null,
    [agentTrace],
  );

  return (
    <>
      {isAgent && (
        <div className="flex items-center gap-2 mb-2 text-[11px] font-mono">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-50 dark:bg-brand-700/20 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-700/40">
            <Sparkles className="w-3 h-3" /> Agent-Mode
          </span>
        </div>
      )}
      {digestEv && <DigestCard ev={digestEv} />}
      {planEv && <PlanCard ev={planEv} />}
      {storylineEv && <StorylineCard ev={storylineEv} />}
      {streaming && liveStatus && (
        <div className="mb-2 flex items-center gap-2 text-sm text-ink-50/70 bg-cream-100/60 dark:bg-ink-700/30 rounded-md px-2.5 py-1.5 border border-cream-200/70 dark:border-ink-600/60">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-500 shrink-0" />
          <span className="flex-1 truncate">{liveStatus}</span>
          {onCancel && (
            <button
              onClick={onCancel}
              className="inline-flex items-center gap-1 text-[11px] text-ink-50/70 hover:text-danger transition-colors"
              title="Agent stoppen"
            >
              <Square className="w-3 h-3" /> Stop
            </button>
          )}
        </div>
      )}
      <Md onCite={onCite} visualSources={visualSources}>
        {streaming && !isAgent ? content + "▍" : s.answer}
      </Md>
      {presentationCards.map((p, i) => (
        <PresentationArtifactCard key={i} {...p} />
      ))}
      {showStructured && rows && rows.length > 0 && cols && (
        <ResultTable title={s.result_title} rows={rows} cols={cols} />
      )}
      {showStructured && s._sql_text && (
        <SqlFootnote sql={s._sql_text} plan={s._query_plan ?? null} />
      )}
      {showStructured && s.footnotes && s.footnotes.length > 0 && (
        <Footnotes notes={s.footnotes} />
      )}
      {agentTrace && agentTrace.length > 0 && (
        <TraceView events={agentTrace} />
      )}
    </>
  );
}

// Agent v2 (Atlas-Task ab56f6b4): collapsed Digest-Card oben in der Reply.
// Topic + intent + expected-deliverable sind die wichtigsten Felder fuer
// 'sieht der Agent wirklich den Kontext?'-Check.
function DigestCard({ ev }: { ev: TraceEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="my-2 rounded-md border border-brand-100 dark:border-brand-700/30 bg-brand-50/30 dark:bg-brand-700/5 text-xs">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 text-left flex items-start gap-2 hover:bg-brand-50/60 dark:hover:bg-brand-700/10 transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3 mt-0.5 shrink-0" /> : <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xs uppercase tracking-wide opacity-60">
            Kontext-Digest
          </div>
          <div className="text-ink mt-0.5 line-clamp-1">
            {ev.topic ?? "(kein Topic)"}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 text-ink-50/80">
          {ev.user_intent && (
            <div><span className="font-semibold">User-Intent:</span> {ev.user_intent}</div>
          )}
          {ev.expected_deliverable && (
            <div><span className="font-semibold">Erwartet:</span> {ev.expected_deliverable}</div>
          )}
          {ev.key_facts && ev.key_facts.length > 0 && (
            <div>
              <div className="font-semibold">Key-Facts:</div>
              <ul className="ml-3 list-disc">
                {ev.key_facts.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}
          {ev.open_questions && ev.open_questions.length > 0 && (
            <div>
              <div className="font-semibold">Offene Fragen:</div>
              <ul className="ml-3 list-disc">
                {ev.open_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}
          {ev._fallback && (
            <div className="italic opacity-60">(fallback-digest — LLM-Call schlug fehl)</div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanCard({ ev }: { ev: TraceEvent }) {
  const [open, setOpen] = useState(false);
  const stepCount = (ev.steps ?? []).length;
  return (
    <div className="my-2 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50/60 dark:bg-ink-800/40 text-xs">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 text-left flex items-start gap-2 hover:bg-cream-100/60 dark:hover:bg-ink-700/30 transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3 mt-0.5 shrink-0" /> : <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xs uppercase tracking-wide opacity-60">
            Plan ({stepCount} {stepCount === 1 ? "Schritt" : "Schritte"})
            {ev.deliverable && <span className="ml-2 px-1 py-0.5 rounded-md bg-brand-100 dark:bg-brand-700/30 text-brand-700 dark:text-brand-300 normal-case tracking-normal">{ev.deliverable}</span>}
          </div>
          <div className="text-ink mt-0.5 line-clamp-1">
            {ev.rationale ?? ev.topic ?? "Plan formuliert"}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 text-ink-50/80">
          {ev.spannungsbogen && (
            <div className="italic">↳ {ev.spannungsbogen}</div>
          )}
          {(ev.steps ?? []).map((step, i) => (
            <div key={i} className="border-l-2 border-brand-200 dark:border-brand-700/40 pl-2">
              <div>
                <span className="font-mono text-[10px] opacity-50 mr-1">#{i + 1}</span>
                {step.tool && (
                  <span className="font-mono text-xs uppercase tracking-wide px-1 py-0.5 rounded-md bg-cream-200 dark:bg-ink-700 text-ink-50 mr-2">
                    {step.tool}
                  </span>
                )}
                <span className="text-ink">{step.step}</span>
              </div>
              {step.acceptance_criteria && (
                <div className="text-[11px] opacity-70 mt-0.5">
                  ↳ akzeptiert wenn: {step.acceptance_criteria}
                </div>
              )}
            </div>
          ))}
          {ev._fallback && (
            <div className="italic opacity-60">(fallback-plan — LLM-Call schlug fehl)</div>
          )}
        </div>
      )}
    </div>
  );
}

// Agent v2 v2: Storyline-Blueprint-Card. Wird VOR create_presentation
// emittiert und zeigt explizit Title + Hook + Slide-Arc + Closing.
function StorylineCard({ ev }: { ev: TraceEvent }) {
  const [open, setOpen] = useState(false);
  const arc = ev.slide_arc ?? [];
  return (
    <div className="my-2 rounded-md border border-cream-300 dark:border-ink-500 bg-cream-100/40 dark:bg-ink-700/30 text-xs">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 text-left flex items-start gap-2 hover:bg-cream-100/80 dark:hover:bg-ink-700/50 transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3 mt-0.5 shrink-0" /> : <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xs uppercase tracking-wide opacity-60">
            Storyline-Blueprint ({arc.length} {arc.length === 1 ? "Slide" : "Slides"})
          </div>
          <div className="text-ink mt-0.5 line-clamp-1 font-medium">
            {ev.title ?? "Slide-Arc"}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 text-ink-50/80">
          {ev.subtitle && (
            <div className="italic text-ink-50/70">{ev.subtitle}</div>
          )}
          {ev.hook && (
            <div><span className="font-semibold">Hook:</span> {ev.hook}</div>
          )}
          {arc.length > 0 && (
            <div>
              <div className="font-semibold mb-1">Slide-Arc:</div>
              <ol className="space-y-1.5">
                {arc.map((s, i) => (
                  <li key={i} className="border-l-2 border-cream-300 dark:border-ink-500 pl-2">
                    <div>
                      <span className="font-mono text-[10px] opacity-50 mr-1">#{s.position ?? i + 1}</span>
                      {s.theme && (
                        <span className="font-mono text-xs uppercase tracking-wide px-1 py-0.5 rounded-md bg-cream-200 dark:bg-ink-700 text-ink-50 mr-2">
                          {s.theme}
                        </span>
                      )}
                      <span className="text-ink">{s.key_message}</span>
                    </div>
                    {s.hero_element && (
                      <div className="text-[11px] opacity-70 mt-0.5">↳ Hero: {s.hero_element}</div>
                    )}
                    {s.supporting_facts && s.supporting_facts.length > 0 && (
                      <ul className="text-[11px] opacity-60 mt-0.5 ml-3 list-disc">
                        {s.supporting_facts.map((sf, j) => <li key={j}>{sf}</li>)}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {ev.closing_message && (
            <div><span className="font-semibold">Closing:</span> {ev.closing_message}</div>
          )}
          {ev._fallback && (
            <div className="italic opacity-60">(fallback-blueprint — LLM-Call schlug fehl)</div>
          )}
        </div>
      )}
    </div>
  );
}

// D6 Inline-Artefakt-Card aus create_presentation tool_result.
interface PresentationArtifact {
  presentation_id: string;
  deep_link: string | null;
}

function extractPresentationArtifacts(
  trace: TraceEvent[] | null | undefined,
): PresentationArtifact[] {
  if (!trace) return [];
  const out: PresentationArtifact[] = [];
  for (const ev of trace) {
    if (ev.type === "tool_result" && ev.tool === "create_presentation" && ev.presentation_id) {
      out.push({
        presentation_id: ev.presentation_id,
        deep_link: ev.deep_link ?? null,
      });
    }
  }
  return out;
}

function PresentationArtifactCard({
  presentation_id,
}: PresentationArtifact) {
  // Frontend-Route ist /pres/:id (NICHT /presentations/ damit kein API-Clash).
  const navigate = useNavigate();
  return (
    <div className="my-3 p-3 rounded-lg border border-brand-200 dark:border-brand-700/40 bg-brand-50/40 dark:bg-brand-700/10 flex items-center gap-3">
      <div className="w-10 h-10 rounded-md bg-brand-100 dark:bg-brand-700/30 grid place-items-center shrink-0">
        <Sparkles className="w-5 h-5 text-brand-600 dark:text-brand-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink">
          Praesentation erstellt
        </div>
        <div className="text-[11px] font-mono text-ink-50/60 truncate">
          {presentation_id}
        </div>
      </div>
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={() => navigate(`/pres/${presentation_id}`)}
      >
        Öffnen →
      </Button>
    </div>
  );
}

// D6 Expandable Trace-View — eingeklappt zeigt Anzahl Schritte, ausgeklappt
// die volle Liste mit Iter-Nr + Tool + Input + Output.
function TraceView({ events }: { events: TraceEvent[] }) {
  const [open, setOpen] = useState(false);
  const stepCount = events.filter((e) =>
    ["tool_use", "initial_retrieve", "reasoning", "final"].includes(e.type),
  ).length;
  return (
    <div className="mt-3 text-xs">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-ink-50/60 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Schritte ansehen ({stepCount})
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 pl-5 border-l-2 border-cream-200 dark:border-ink-600">
          {events.map((ev, i) => (
            <TraceRow key={i} ev={ev} />
          ))}
        </div>
      )}
    </div>
  );
}

function TraceRow({ ev }: { ev: TraceEvent }) {
  const [open, setOpen] = useState(false);
  const meta = traceRowMeta(ev);
  const expandable = Boolean(meta.detail);
  return (
    <div className="text-ink-50/80">
      <button
        onClick={() => expandable && setOpen((v) => !v)}
        disabled={!expandable}
        className={
          "w-full text-left flex items-start gap-1.5 " +
          (expandable ? "hover:text-ink cursor-pointer" : "cursor-default")
        }
      >
        <span className="font-mono text-[10px] opacity-50 mt-0.5 w-7 shrink-0">
          {ev.iteration ? `#${ev.iteration}` : "·"}
        </span>
        <span className="font-mono text-xs uppercase tracking-wide opacity-70 mt-0.5 w-20 shrink-0">
          {meta.label}
        </span>
        <span className="flex-1 leading-snug">{meta.head}</span>
        {expandable && (
          <ChevronRight
            className={
              "w-3 h-3 mt-0.5 shrink-0 transition-transform " +
              (open ? "rotate-90" : "")
            }
          />
        )}
      </button>
      {open && meta.detail && (
        <div className="mt-1 ml-[8.25rem] p-2 rounded-md bg-cream-100/50 dark:bg-ink-700/30 font-mono text-[10px] whitespace-pre-wrap break-words text-ink-50/80 max-h-64 overflow-auto">
          {meta.detail}
        </div>
      )}
    </div>
  );
}

function traceRowMeta(ev: TraceEvent): { label: string; head: string; detail: string | null } {
  switch (ev.type) {
    case "trigger":
      return { label: "trigger", head: `Trigger erkannt: ${ev.matched ?? "?"}`, detail: null };
    case "digest":
      return {
        label: "digest",
        head: ev.topic ? `Topic: ${ev.topic.slice(0, 90)}` : "Conversation-Digest erstellt",
        detail: JSON.stringify({
          topic: ev.topic, user_intent: ev.user_intent,
          expected_deliverable: ev.expected_deliverable,
          key_facts: ev.key_facts, open_questions: ev.open_questions,
        }, null, 2),
      };
    case "plan":
      return {
        label: "plan",
        head: `${ev.deliverable ?? "Plan"} (${(ev.steps ?? []).length} Schritte)`,
        detail: ev.spannungsbogen
          ? `Spannungsbogen: ${ev.spannungsbogen}\n\n${JSON.stringify({ rationale: ev.rationale, steps: ev.steps }, null, 2)}`
          : JSON.stringify({ rationale: ev.rationale, steps: ev.steps }, null, 2),
      };
    case "plan_error":
      return { label: "error", head: "Plan-Step fehlgeschlagen", detail: ev.error ?? null };
    case "storyline":
      return {
        label: "storyline",
        head: ev.title ?? "Storyline-Blueprint",
        detail: JSON.stringify({
          subtitle: ev.subtitle, hook: ev.hook,
          slide_arc: ev.slide_arc, closing: ev.closing_message,
        }, null, 2),
      };
    case "storyline_error":
      return { label: "error", head: "Storyline-Blueprint fehlgeschlagen", detail: ev.error ?? null };
    case "initial_retrieve": {
      const kinds = ev.kinds ?? {};
      const summary = Object.entries(kinds).map(([k, v]) => `${k}=${v}`).join(", ") || "—";
      return {
        label: "retrieve",
        head: `${ev.hits ?? 0} Treffer initial (${summary})`,
        detail: null,
      };
    }
    case "initial_retrieve_error":
      return { label: "error", head: "Initial-Retrieval fehlgeschlagen", detail: ev.error ?? null };
    case "thinking":
      return { label: "thinking", head: `LLM denkt (${ev.model ?? "—"})`, detail: null };
    case "reasoning":
      return {
        label: "plan",
        head: ev.text ? ev.text.slice(0, 90) + (ev.text.length > 90 ? "…" : "") : "Plan formuliert",
        detail: ev.text ?? null,
      };
    case "tool_use":
      return {
        label: ev.tool ?? "tool",
        head: `Tool-Call: ${ev.tool ?? "?"}`,
        detail: ev.input ? JSON.stringify(ev.input, null, 2) : null,
      };
    case "tool_result":
      return {
        label: ev.tool ?? "tool",
        head: ev.output_summary ?? "Tool-Result",
        detail: ev.presentation_id ? `presentation_id: ${ev.presentation_id}\ndeep_link: ${ev.deep_link ?? "—"}` : null,
      };
    case "final":
      return {
        label: "final",
        head: `Antwort fertig (${ev.tokens_in ?? 0}+${ev.tokens_out ?? 0} tok, stop=${ev.stop_reason ?? "—"})`,
        detail: null,
      };
    case "cancelled":
      return { label: "cancel", head: `Abgebrochen: ${ev.reason ?? "—"}`, detail: null };
    case "llm_error":
    case "stream_error":
      return { label: "error", head: "LLM-Fehler", detail: ev.error ?? null };
    case "iteration_cap_reached":
      return {
        label: "limit",
        head: `Iter-Cap erreicht (${ev.tokens_in_total ?? 0}+${ev.tokens_out_total ?? 0} tok)`,
        detail: null,
      };
    default:
      return { label: ev.type, head: JSON.stringify(ev).slice(0, 80), detail: JSON.stringify(ev, null, 2) };
  }
}

function buildVisualSources(
  sources: SourceLike[] | undefined
): Map<number, InlineVisualSource> | undefined {
  if (!sources?.length) return undefined;
  const map = new Map<number, InlineVisualSource>();
  sources.forEach((s, i) => {
    const n = i + 1;
    const k = s.kind ?? "text";
    if (k === "figure" && s.document_id && s.fig_id && s.page != null) {
      map.set(n, {
        kind: "figure",
        n,
        document_id: s.document_id,
        fig_id: s.fig_id,
        page: s.page,
        caption: s.caption ?? null,
        source_hash: s.source_hash ?? null,
        external_source_slug: s.external_source_slug ?? null,
      });
    } else if (k === "table" && s.document_id && s.table_id && s.page != null) {
      map.set(n, {
        kind: "table",
        n,
        document_id: s.document_id,
        table_id: s.table_id,
        page: s.page,
        caption: s.caption ?? null,
        summary: s.summary ?? null,
        html_preview: s.html_preview ?? null,
        source_hash: s.source_hash ?? null,
        external_source_slug: s.external_source_slug ?? null,
      });
    }
  });
  return map.size > 0 ? map : undefined;
}

function ResultTable({
  title,
  rows,
  cols,
}: {
  title?: string | null;
  rows: Record<string, unknown>[];
  cols: string[];
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="mt-3 border border-border rounded-lg overflow-hidden bg-surface">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-1.5 bg-surface-2 text-xs font-medium border-b border-border"
      >
        <ChevronRight
          className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`}
        />
        <span className="flex-1 text-left">
          {title || "Ergebnis"}{" "}
          <span className="text-text-3">({rows.length})</span>
        </span>
      </button>
      {expanded && (
        <div className="max-h-[440px] overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-surface-2/70 sticky top-0">
              <tr>
                {cols.map((c) => (
                  <th
                    key={c}
                    className="text-left px-3 py-1.5 font-medium text-text-2 border-b border-border"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-surface-2/50">
                  {cols.map((c) => (
                    <td
                      key={c}
                      className="px-3 py-1.5 align-top text-text"
                    >
                      {formatCell(r[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SqlFootnote({ sql, plan }: { sql: string; plan: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-text-2 hover:text-brand-600 inline-flex items-center gap-1"
      >
        <ChevronRight
          className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`}
        />
        <Code2 className="w-3 h-3" />
        {open ? "SQL ausblenden" : "SQL anzeigen"}
      </button>
      {open && (
        <div className="mt-1.5 border border-border rounded-md bg-surface-2 overflow-hidden">
          {plan && (
            <div className="px-3 py-1.5 text-[11px] text-text-2 border-b border-border italic">
              {plan}
            </div>
          )}
          <pre className="px-3 py-2 text-[11px] leading-relaxed font-mono text-text overflow-x-auto whitespace-pre-wrap">
            {sql}
          </pre>
        </div>
      )}
    </div>
  );
}

function Footnotes({ notes }: { notes: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-text-2 hover:text-brand-600 inline-flex items-center gap-1"
      >
        <ChevronRight
          className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`}
        />
        {notes.length} {notes.length === 1 ? "Quelle" : "Quellen"}
      </button>
      {open && (
        <ol className="mt-1 ml-4 text-[11px] text-text-2 list-decimal space-y-0.5">
          {notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
